/**
 * DevicePairingService.js
 *
 * Handles the full pairing lifecycle between a web user and a physical device:
 *  - Generates short-lived 6-digit pairing codes stored in Firebase RTDB
 *  - Exchanges a pairing code for a device-specific session credential
 *  - Writes/revokes pairing session records the device can listen to in real-time
 *  - Tracks user geolocation and triggers auto-unpair when outside geofence radius
 */

import { getDatabase, ref, set, get, remove, onValue, off } from 'firebase/database';

// ─── Constants ──────────────────────────────────────────────────────────────

/** How long (ms) a pairing code remains valid before it expires */
const PAIRING_CODE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/** How far (metres) the user may wander before auto-unpair triggers */
const GEOFENCE_RADIUS_METERS = 30;

/** How many consecutive out-of-range readings before we unpair */
const GEOFENCE_VIOLATION_COUNT = 3;

/** Interval (ms) between geolocation polls */
const GEO_POLL_INTERVAL_MS = 15_000;

// ─── Pairing Code Generation ─────────────────────────────────────────────────

/**
 * Generate a cryptographically random 6-digit numeric pairing code.
 * @returns {string} e.g. "047291"
 */
export function generatePairingCode() {
  const arr = new Uint32Array(1);
  window.crypto.getRandomValues(arr);
  return String(arr[0] % 1_000_000).padStart(6, '0');
}

// ─── Firebase Pairing Record Helpers ────────────────────────────────────────

/**
 * Write a pairing offer to Firebase so the device can poll/listen for it.
 *
 * Path: pairings/{deviceId}/offer
 * {
 *   code,          // 6-digit string the user will enter on the device
 *   userId,        // Firebase UID of the initiating user
 *   expiresAt,     // Unix ms timestamp after which the code is invalid
 *   createdAt,
 * }
 *
 * @param {string} deviceId   FHIR Device resource id
 * @param {string} userId     Firebase auth uid
 * @param {string} code       6-digit pairing code
 */
export async function writePairingOffer(deviceId, userId, code) {
  const db = getDatabase();
  const offerRef = ref(db, `pairings/${deviceId}/offer`);
  await set(offerRef, {
    code,
    userId,
    expiresAt: Date.now() + PAIRING_CODE_TTL_MS,
    createdAt: Date.now(),
  });
}

/**
 * Validate a pairing offer:
 *  - Code matches
 *  - Code has not expired
 *
 * @param {string} deviceId
 * @param {string} enteredCode
 * @returns {{ valid: boolean, offer?: object, error?: string }}
 */
export async function validatePairingCode(deviceId, enteredCode) {
  const db = getDatabase();
  const offerRef = ref(db, `pairings/${deviceId}/offer`);
  const snap = await get(offerRef);

  if (!snap.exists()) {
    return { valid: false, error: 'No pairing offer found for this device.' };
  }

  const offer = snap.val();

  if (offer.code !== enteredCode) {
    return { valid: false, error: 'Incorrect pairing code.' };
  }

  if (Date.now() > offer.expiresAt) {
    return { valid: false, error: 'Pairing code has expired. Please generate a new one.' };
  }

  return { valid: true, offer };
}

/**
 * Activate a pairing session after code validation.
 * Writes the active session the device listens to in real-time.
 *
 * Path: pairings/{deviceId}/session
 * {
 *   userId,
 *   pairedAt,
 *   active: true,
 * }
 *
 * Also clears the offer so the code cannot be reused.
 *
 * @param {string} deviceId
 * @param {string} userId
 */
export async function activatePairingSession(deviceId, userId) {
  const db = getDatabase();

  // Write active session
  await set(ref(db, `pairings/${deviceId}/session`), {
    userId,
    pairedAt: Date.now(),
    active: true,
  });

  // Remove the one-time offer
  await remove(ref(db, `pairings/${deviceId}/offer`));
}

/**
 * Revoke/unpair the active session for a device.
 * The device, listening to this path, will detect active becoming false and unpair.
 *
 * @param {string} deviceId
 * @param {string} reason  - 'user_initiated' | 'geofence' | 'timeout'
 */
export async function revokePairingSession(deviceId, reason = 'user_initiated') {
  const db = getDatabase();
  await set(ref(db, `pairings/${deviceId}/session`), {
    active: false,
    revokedAt: Date.now(),
    reason,
  });
}

/**
 * Subscribe to real-time pairing session changes for a device.
 *
 * @param {string} deviceId
 * @param {function} callback  - called with the session value whenever it changes
 * @returns {function} unsubscribe function
 */
export function subscribePairingSession(deviceId, callback) {
  const db = getDatabase();
  const sessionRef = ref(db, `pairings/${deviceId}/session`);

  onValue(sessionRef, (snap) => {
    callback(snap.exists() ? snap.val() : null);
  });

  return () => off(sessionRef);
}

// ─── Geofencing ──────────────────────────────────────────────────────────────

/**
 * Calculate the great-circle distance between two lat/lon points (Haversine formula).
 * @returns distance in metres
 */
function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6_371_000; // Earth radius in metres
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Start geofencing for a paired device.
 * Polls the browser Geolocation API and revokes the pairing session when the
 * user has been outside `GEOFENCE_RADIUS_METERS` for `GEOFENCE_VIOLATION_COUNT`
 * consecutive checks.
 *
 * @param {object} params
 * @param {string}   params.deviceId
 * @param {number}   params.deviceLat   - Device's known latitude (stored at pair-time)
 * @param {number}   params.deviceLon   - Device's known longitude
 * @param {function} params.onOutOfRange - called when auto-unpair triggers
 * @param {function} params.onLocationUpdate - called each poll with { lat, lon, distance, inRange }
 * @returns {function} stopGeofencing — call this to stop the polling
 */
export function startGeofencing({ deviceId, deviceLat, deviceLon, onOutOfRange, onLocationUpdate }) {
  let violationCount = 0;
  let intervalId = null;
  let stopped = false;

  const poll = () => {
    if (stopped) return;

    if (!navigator.geolocation) {
      console.warn('DevicePairingService: Geolocation not supported in this browser.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (stopped) return;

        const { latitude, longitude } = position.coords;
        const distance = haversineDistance(latitude, longitude, deviceLat, deviceLon);
        const inRange = distance <= GEOFENCE_RADIUS_METERS;

        onLocationUpdate?.({ lat: latitude, lon: longitude, distance, inRange });

        if (!inRange) {
          violationCount++;
          if (violationCount >= GEOFENCE_VIOLATION_COUNT) {
            stopped = true;
            clearInterval(intervalId);
            revokePairingSession(deviceId, 'geofence').then(() => {
              onOutOfRange?.({ distance });
            });
          }
        } else {
          violationCount = 0; // Reset on re-entry
        }
      },
      (err) => {
        console.warn('DevicePairingService: Geolocation error:', err.message);
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 5_000 }
    );
  };

  // Run immediately, then on interval
  poll();
  intervalId = setInterval(poll, GEO_POLL_INTERVAL_MS);

  return () => {
    stopped = true;
    clearInterval(intervalId);
  };
}

export { PAIRING_CODE_TTL_MS, GEOFENCE_RADIUS_METERS, GEO_POLL_INTERVAL_MS };
